import { Injectable, NgZone } from '@angular/core';
import { MapsAPILoader } from '@agm/core';
import { Observable } from 'rxjs';
import { ZoneLocation } from './alarm-zone.model';

declare var google: any;

/**
 * Jedyne miejsce w aplikacji, które wie o istnieniu google.maps.Geocoder.
 *
 * Backend nie geokoduje niczego — adres jest u niego wyłącznie etykietą tekstową,
 * a do opaski leci `13. ZONE:lat,lon,promień,interwał`. Front jest więc jedynym
 * źródłem współrzędnych i to od tej usługi zależy, czy ręcznie wpisany adres
 * w ogóle da się zapisać.
 */
@Injectable()
export class GeocodingService {

  constructor(
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
  ) {}

  /**
   * Zamienia tekst adresu na lokalizację. Emituje `null`, gdy Google nie zna adresu
   * albo gdy API jest niedostępne (np. wyłączone Geocoding API na kluczu) — wołający
   * traktuje oba przypadki tak samo: nie ma współrzędnych, nie ma czego zapisać.
   */
  geocode(address: string): Observable<ZoneLocation | null> {
    return new Observable<ZoneLocation | null>(subscriber => {
      let cancelled = false;

      this.mapsAPILoader.load().then(() => {
        if (cancelled) {
          return;
        }
        new google.maps.Geocoder().geocode({ address: address }, (results, status) => {
          if (cancelled) {
            return;
          }
          // Callback Google żyje poza strefą Angulara — bez tego widok się nie odświeży.
          this.ngZone.run(() => {
            if (String(status) === 'OK' && results && results.length) {
              const location = results[0].geometry.location;
              subscriber.next({
                address: results[0].formatted_address,
                latitude: location.lat(),
                longitude: location.lng()
              });
            } else {
              subscriber.next(null);
            }
            subscriber.complete();
          });
        });
      }).catch(() => {
        if (cancelled) {
          return;
        }
        this.ngZone.run(() => {
          subscriber.next(null);
          subscriber.complete();
        });
      });

      // Geokodowanie w locie po opuszczeniu ekranu nie może dotknąć martwego widoku.
      return () => { cancelled = true; };
    });
  }
}
